import * as React from 'react'

import { ShoppingBag } from 'react-feather'
import { useTranslation } from 'react-i18next'
import { RouteComponentProps, withRouter } from 'react-router-dom'

import { AppContext, useWhitelabel } from 'main/framework'
import client from 'main/framework/apolloClient'
import { CredentialsFields } from 'main/gql/credentials'
import { NAV_MODALS } from 'main/hooks/useSSOContext'
import { palette } from 'main/ui'
import { track } from 'main/util/analytics/segment'
import { login } from 'main/util/auth'
import { Product, productEnabled } from 'main/util/networks'

import { LOCIZE_NAMESPACE } from '../../../../../common'
import { i18nKeyPrefix } from '../../../i18n'
import CTAButton from '../common/CTAButton'
import useNavContextModal from '../common/useNavContextModal'
import Content from './Content'
import { School } from './types'

const PRE = `${i18nKeyPrefix}-CTA-ENROLL`

interface IProps extends RouteComponentProps {
  disabled: boolean
  full: boolean
  school: School
  showForCcms2: boolean
}

function Enroll(props: IProps) {
  const { disabled, full, history, school, location, showForCcms2 } = props

  const { t, i18n } = useTranslation(LOCIZE_NAMESPACE)

  const appContext = React.useContext(AppContext)

  const whitelabel = useWhitelabel()
  const instantEnrollEnabled: boolean = productEnabled(
    whitelabel.enabledProducts,
    Product.INSTANT_ENROLL,
  )

  const [isModalOpen, openModal, closeModal] = useNavContextModal(
    NAV_MODALS.ENROLL,
    location.pathname,
  )

  const title: string = React.useMemo(() => generateTitle(full), [i18n.resolvedLanguage])

  const onClickCTA = React.useCallback(() => {
    track('[BEHAVIOR] Instant enroll modal opened', {
      schoolId: school.id,
      userId: appContext.user?.id,
    })
    openModal()
  }, [appContext, track, openModal])
  if (!showForCcms2 && school.migratedToWsCenters) return null
  if (instantEnrollEnabled && (!school.hasFundingAccount || !school.hasAvailability)) return null

  return (
    <>
      <CTAButton
        full={full}
        icon={<ShoppingBag color={disabled ? palette.white : palette.skyBlue2} />}
        kind={disabled ? 'regular' : 'outline'}
        label={title}
        pendo={
          instantEnrollEnabled ? 'school-instant-enroll-button' : 'school-apply-to-enroll-button'
        }
        onClick={onClickCTA}
      />
      {isModalOpen && (
        <Content
          {...props}
          title={title}
          instantEnroll={instantEnrollEnabled}
          onAuth={async (credentials: CredentialsFields) => {
            login(client, credentials, appContext)
          }}
          onComplete={onComplete}
          onRequestClose={() => closeModal()}
        />
      )}
    </>
  )

  function onComplete() {
    const path = instantEnrollEnabled ? `/instant-enroll/` : `/application/`
    history.push(path.concat(`${school.id}`))
  }

  function generateTitle(fullScreen: boolean) {
    const fullTitle: string = instantEnrollEnabled
      ? t(`${PRE}-INSTANT-ENROLL`)
      : t(`${PRE}-APPLY-ENROLL`)
    const mobileTitle: string = instantEnrollEnabled
      ? t(`${PRE}-MOBILE-INSTANT-ENROLL`)
      : t(`${PRE}-MOBILE-APPLY-ENROLL`)

    return fullScreen ? fullTitle : mobileTitle
  }
}

export default withRouter(Enroll)
